import {
  Bodies,
  Body,
  Composite,
  Engine,
  Events,
  Query,
  Vector,
  type IEventCollision,
} from 'matter-js'
import { GAME_CONFIG, clamp } from '../config/game-config'
import {
  applyDamage,
  calculateCollisionDamage,
  calculateHealthBaseline,
  calculateRelativeBalance,
  calculateScore,
  type HealthBaseline,
} from '../domain/rules'
import type {
  ArenaSnapshot,
  BobbleKind,
  BobbleSnapshot,
  BobbleState,
  ExplosionResult,
  Point,
} from '../domain/types'

interface BobbleRecord extends BobbleState {
  body: Body
  size: number
}

export type WorldEvent =
  | { type: 'damage'; id: number; amount: number; at: Point }
  | { type: 'death'; id: number; kind: BobbleKind; at: Point }

const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0
  return () => {
    state += 0x6d2b79f5
    let value = state
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296
  }
}

const copyPoint = (point: Point): Point => ({ x: point.x, y: point.y })

export class GameWorld {
  readonly engine = Engine.create({ enableSleeping: false })

  private readonly records = new Map<number, BobbleRecord>()
  private readonly pendingRemoval = new Set<number>()
  private readonly eventQueue: WorldEvent[] = []
  private readonly random: () => number
  private readonly dynamicBodies: Body[] = []
  private healthBaseline: HealthBaseline = { square: 0, circle: 0 }
  private nextBobbleId = 1
  private bodySize = 48

  constructor(
    readonly width: number,
    readonly height: number,
    seed = Date.now(),
  ) {
    this.random = createRandom(seed)
    this.engine.gravity.x = 0
    this.engine.gravity.y = 0
    this.engine.gravity.scale = 0
    this.engine.positionIterations = 8
    this.engine.velocityIterations = 8

    this.createWalls()
    this.spawnInitialBobbles()
    Events.on(this.engine, 'collisionStart', (event) => this.onCollisionStart(event))
  }

  get score(): number {
    return calculateScore(this.aliveStates())
  }

  get relativeBalance(): number {
    return calculateRelativeBalance(this.aliveStates(), this.healthBaseline)
  }

  get bestPossibleBalance(): number {
    let squareHealth = 0
    for (const record of this.records.values()) {
      if (record.alive && record.kind === 'square') squareHealth += Math.max(0, record.health)
    }
    return this.healthBaseline.square > 0 ? (squareHealth / this.healthBaseline.square) * 100 : 0
  }

  get aliveCount(): number {
    return this.records.size
  }

  count(kind: BobbleKind): number {
    let result = 0
    for (const record of this.records.values()) {
      if (record.alive && record.kind === kind) result += 1
    }
    return result
  }

  step(deltaMs = GAME_CONFIG.fixedStepMs): void {
    Engine.update(this.engine, deltaMs)
    this.flushRemovals()
  }

  explode(visualCenter: Point): ExplosionResult {
    const aliveBodies = this.dynamicBodies.filter((body) => this.records.has(body.id))
    const hit = Query.point(aliveBodies, visualCenter)[0]
    const protectedRecord = hit ? this.records.get(hit.id) : undefined
    const physicsCenter = protectedRecord ? copyPoint(protectedRecord.body.position) : copyPoint(visualCenter)
    const affected: number[] = []

    for (const record of this.records.values()) {
      if (!record.alive || record.id === protectedRecord?.id) continue

      const offset = Vector.sub(record.body.position, physicsCenter)
      const distance = Vector.magnitude(offset)
      if (distance < 0.001) continue

      const bodyScale = this.bodySize / GAME_CONFIG.referenceBodySize
      const safeDistance = Math.max(distance, GAME_CONFIG.explosionMinimumDistance * bodyScale)
      const velocityDelta = Math.min(
        GAME_CONFIG.explosionMaximumVelocityDelta * bodyScale,
        (GAME_CONFIG.explosionPower * bodyScale * bodyScale) / (safeDistance * safeDistance),
      )
      const direction = Vector.normalise(offset)
      const nextVelocity = Vector.add(record.body.velocity, Vector.mult(direction, velocityDelta))
      const speed = Vector.magnitude(nextVelocity)
      const maximumSpeed = GAME_CONFIG.maximumBodySpeed * bodyScale

      Body.setVelocity(
        record.body,
        speed > maximumSpeed ? Vector.mult(Vector.normalise(nextVelocity), maximumSpeed) : nextVelocity,
      )
      Body.setAngularVelocity(
        record.body,
        clamp(record.body.angularVelocity + direction.x * 0.035 - direction.y * 0.025, -0.22, 0.22),
      )
      affected.push(record.id)
    }

    return {
      affected,
      protectedId: protectedRecord?.id ?? null,
      physicsCenter,
      visualCenter: copyPoint(visualCenter),
    }
  }

  snapshot(): ArenaSnapshot {
    const bobbles: BobbleSnapshot[] = []
    for (const record of this.records.values()) {
      bobbles.push({
        id: record.id,
        kind: record.kind,
        health: record.health,
        maxHealth: record.maxHealth,
        alive: record.alive,
        position: copyPoint(record.body.position),
        velocity: copyPoint(record.body.velocity),
        angle: record.body.angle,
        size: record.size,
      })
    }

    return { width: this.width, height: this.height, bobbles }
  }

  drainEvents(): WorldEvent[] {
    return this.eventQueue.splice(0)
  }

  destroy(): void {
    Events.off(this.engine, 'collisionStart')
    Composite.clear(this.engine.world, false, true)
    Engine.clear(this.engine)
    this.records.clear()
    this.dynamicBodies.length = 0
  }

  private aliveStates(): BobbleState[] {
    return [...this.records.values()].map(({ id, kind, health, maxHealth, alive }) => ({
      id,
      kind,
      health,
      maxHealth,
      alive,
    }))
  }

  private createWalls(): void {
    const thickness = GAME_CONFIG.wallThickness
    const options = {
      isStatic: true,
      restitution: GAME_CONFIG.restitution,
      friction: GAME_CONFIG.friction,
      label: 'arena-wall',
    }
    const walls = [
      Bodies.rectangle(this.width / 2, -thickness / 2, this.width + thickness * 2, thickness, options),
      Bodies.rectangle(
        this.width / 2,
        this.height + thickness / 2,
        this.width + thickness * 2,
        thickness,
        options,
      ),
      Bodies.rectangle(-thickness / 2, this.height / 2, thickness, this.height, options),
      Bodies.rectangle(
        this.width + thickness / 2,
        this.height / 2,
        thickness,
        this.height,
        options,
      ),
    ]
    Composite.add(this.engine.world, walls)
  }

  private spawnInitialBobbles(): void {
    const areaScale = Math.sqrt(this.width * this.height / GAME_CONFIG.referenceArenaArea)
    this.bodySize = clamp(GAME_CONFIG.referenceBodySize * areaScale, 24, 62)
    const count = GAME_CONFIG.initialSquares + GAME_CONFIG.initialCircles

    for (let index = 0; index < count; index += 1) {
      const kind: BobbleKind = index % 2 === 0 ? 'square' : 'circle'
      this.spawnBobble(kind)
    }
    this.healthBaseline = calculateHealthBaseline(this.aliveStates())
  }

  private spawnBobble(kind: BobbleKind): void {
    const radius = this.bodySize / 2
    const x = this.width * (0.2 + this.random() * 0.6)
    const y = this.height * (0.2 + this.random() * 0.6)
    const options = {
      restitution: GAME_CONFIG.restitution,
      friction: GAME_CONFIG.friction,
      frictionAir: GAME_CONFIG.frictionAir,
      density: 0.001,
      label: `bobble-${kind}`,
    }
    const body =
      kind === 'square'
        ? Bodies.rectangle(x, y, this.bodySize, this.bodySize, options)
        : Bodies.circle(x, y, radius, options)
    const maxHealth =
      GAME_CONFIG.minHealth + this.random() * (GAME_CONFIG.maxHealth - GAME_CONFIG.minHealth)
    const record: BobbleRecord = {
      id: this.nextBobbleId,
      kind,
      health: maxHealth,
      maxHealth,
      alive: true,
      body,
      size: this.bodySize,
    }

    this.nextBobbleId += 1
    const heading = this.random() * Math.PI * 2
    const arenaScale = this.bodySize / GAME_CONFIG.referenceBodySize
    Body.setVelocity(body, {
      x: Math.cos(heading) * GAME_CONFIG.initialSpeed * arenaScale,
      y: Math.sin(heading) * GAME_CONFIG.initialSpeed * arenaScale,
    })
    this.records.set(body.id, record)
    this.dynamicBodies.push(body)
    Composite.add(this.engine.world, body)
  }

  private onCollisionStart(event: IEventCollision<Engine>): void {
    for (const pair of event.pairs) {
      const recordA = this.records.get(pair.bodyA.id)
      const recordB = this.records.get(pair.bodyB.id)
      if (!recordA && !recordB) continue

      const relativeVelocity = Vector.sub(pair.bodyB.velocity, pair.bodyA.velocity)
      const normalSpeed = Math.abs(Vector.dot(relativeVelocity, pair.collision.normal))
      const { damage } = calculateCollisionDamage(normalSpeed)
      if (damage <= 0) continue

      if (recordA) this.damage(recordA, damage)
      if (recordB) this.damage(recordB, damage)
    }
  }

  private damage(record: BobbleRecord, amount: number): void {
    if (!record.alive) return
    const died = applyDamage(record, amount)
    this.eventQueue.push({
      type: 'damage',
      id: record.id,
      amount,
      at: copyPoint(record.body.position),
    })

    if (died) {
      this.pendingRemoval.add(record.body.id)
      this.eventQueue.push({
        type: 'death',
        id: record.id,
        kind: record.kind,
        at: copyPoint(record.body.position),
      })
    }
  }

  private flushRemovals(): void {
    for (const bodyId of this.pendingRemoval) {
      const record = this.records.get(bodyId)
      if (!record) continue
      Composite.remove(this.engine.world, record.body)
      this.records.delete(bodyId)
      const index = this.dynamicBodies.indexOf(record.body)
      if (index >= 0) this.dynamicBodies.splice(index, 1)
    }
    this.pendingRemoval.clear()
  }
}
